/**
 * Authoritative employer map location derived from AI server coords and/or
 * address geocode. Every consumer (KV rows, overview map, Site Inspector)
 * must read from this object so coordinates, source, and distance never disagree.
 */

import { distanceMeters, type LatLngPoint } from './employerSiteViewpoints';

export type LocationSource = 'server' | 'geocode' | 'search';

export type DistanceDerivation = 'server' | 'recomputed' | 'unavailable';

/**
 * Server AI coords that land inside a loose Canada bbox can still be rounded or
 * wrong-street (~150–300 m). Geocode the stated address and compare; above this
 * threshold the operator must see a mismatch rather than a silent wrong pin.
 * 200 m ≈ half a downtown block — tight enough to catch rounded AI coords,
 * loose enough to ignore rooftop vs curb offsets.
 */
export const ADDRESS_COORD_MISMATCH_THRESHOLD_M = 200;

export type CoordAddressMismatch = {
  server: LatLngPoint;
  geocoded: LatLngPoint;
  distanceMeters: number;
  thresholdMeters: number;
};

export type EmployerResolvedLocation = {
  lat: number;
  lng: number;
  source: LocationSource;
  address: string | null;
  distanceKm: number | null;
  distanceDerivation: DistanceDerivation;
  mismatch: CoordAddressMismatch | null;
  /** The non-plotted candidate when mismatch forced a choice. */
  alternate: { lat: number; lng: number; source: LocationSource } | null;
};

/** Continental Canada + nearshore — rejects ocean / swapped-hemisphere junk. */
export function inCanadaBBox(lat: number, lng: number): boolean {
  return lat >= 41.5 && lat <= 83.5 && lng >= -141 && lng <= -52;
}

/**
 * Prefer AI coords when they land in Canada. Fix common model mistakes (lat/lng
 * swap, positive western longitude). Return null for ocean / non-Canada.
 */
export function normalizeCanadaCoords(
  lat: number,
  lng: number,
): LatLngPoint | null {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (inCanadaBBox(lat, lng)) return { lat, lng };
  if (inCanadaBBox(lng, lat)) return { lat: lng, lng: lat };
  if (lng > 0 && inCanadaBBox(lat, -lng)) return { lat, lng: -lng };
  return null;
}

export function haversineKm(a: LatLngPoint, b: LatLngPoint): number {
  return distanceMeters(a, b) / 1000;
}

export function purchaseCentroid(points: LatLngPoint[]): LatLngPoint | null {
  if (points.length === 0) return null;
  return {
    lat: points.reduce((s, p) => s + p.lat, 0) / points.length,
    lng: points.reduce((s, p) => s + p.lng, 0) / points.length,
  };
}

/** Declared employer address from the application record beats AI `possibleEmployer.address` (often HQ). */
export function employerPlotAddress(
  storedAddress: string | null | undefined,
  aiAddress: string | null | undefined,
): string {
  return (storedAddress ?? aiAddress ?? '').trim();
}

/** Stored employer name from the application record beats AI `possibleEmployer.name`. */
export function employerPlotName(
  storedName: string | null | undefined,
  aiName: string | null | undefined,
): string {
  return (storedName ?? aiName ?? '').trim();
}

export type ResolveEmployerLocationInput = {
  serverLat: number | null | undefined;
  serverLng: number | null | undefined;
  /** Geocode of the branch / employer address, when available. */
  geocoded: LatLngPoint | null | undefined;
  address: string | null | undefined;
  /** Server-reported distance; trusted only when plotting unchanged server coords. */
  serverDistanceKm: number | null | undefined;
  purchasePoints: LatLngPoint[];
  /**
   * When server and geocode disagree past the threshold:
   * - 'geocode' (default): plot the address geocode — operators verify street frontage
   *   against the stated branch address; surface server as alternate.
   * - 'server': keep AI pin, surface geocode as alternate (rarely preferred).
   */
  mismatchPlotPolicy?: 'geocode' | 'server';
};

/**
 * Build the single resolved location every UI surface should display.
 *
 * Resolution order (no mismatch):
 * 1. Normalized server coords inside Canada bbox
 * 2. Else geocoded address
 *
 * On mismatch (both present, distance > ADDRESS_COORD_MISMATCH_THRESHOLD_M):
 * plot per mismatchPlotPolicy (default geocode) and attach mismatch metadata.
 */
export function resolveEmployerLocation(
  input: ResolveEmployerLocationInput,
): EmployerResolvedLocation | null {
  const address = (input.address ?? '').trim() || null;
  const server = normalizeCanadaCoords(
    Number(input.serverLat),
    Number(input.serverLng),
  );
  const geocoded =
    input.geocoded &&
    Number.isFinite(input.geocoded.lat) &&
    Number.isFinite(input.geocoded.lng)
      ? normalizeCanadaCoords(input.geocoded.lat, input.geocoded.lng)
      : null;

  let mismatch: CoordAddressMismatch | null = null;
  let plotted: LatLngPoint | null = null;
  let source: LocationSource | null = null;
  let alternate: EmployerResolvedLocation['alternate'] = null;

  if (server && geocoded) {
    const dist = distanceMeters(server, geocoded);
    if (dist > ADDRESS_COORD_MISMATCH_THRESHOLD_M) {
      mismatch = {
        server,
        geocoded,
        distanceMeters: dist,
        thresholdMeters: ADDRESS_COORD_MISMATCH_THRESHOLD_M,
      };
      const preferGeocode = (input.mismatchPlotPolicy ?? 'geocode') === 'geocode';
      if (preferGeocode) {
        plotted = geocoded;
        source = 'geocode';
        alternate = { ...server, source: 'server' };
      } else {
        plotted = server;
        source = 'server';
        alternate = { ...geocoded, source: 'geocode' };
      }
    } else {
      plotted = server;
      source = 'server';
    }
  } else if (server) {
    plotted = server;
    source = 'server';
  } else if (geocoded) {
    plotted = geocoded;
    source = 'geocode';
  }

  if (!plotted || !source) return null;

  const centroid = purchaseCentroid(input.purchasePoints);
  const serverDistanceOk =
    source === 'server' &&
    mismatch == null &&
    input.serverDistanceKm != null &&
    Number.isFinite(input.serverDistanceKm);

  let distanceKm: number | null = null;
  let distanceDerivation: DistanceDerivation = 'unavailable';

  if (serverDistanceOk) {
    distanceKm = input.serverDistanceKm as number;
    distanceDerivation = 'server';
  } else if (centroid) {
    distanceKm = haversineKm(centroid, plotted);
    distanceDerivation = 'recomputed';
  }

  return {
    lat: plotted.lat,
    lng: plotted.lng,
    source,
    address,
    distanceKm,
    distanceDerivation,
    mismatch,
    alternate,
  };
}

/** Apply an operator search override on top of a resolved employer location. */
export function withSearchOverride(
  base: EmployerResolvedLocation | null,
  point: LatLngPoint,
  address: string | null,
  purchasePoints: LatLngPoint[],
): EmployerResolvedLocation {
  const centroid = purchaseCentroid(purchasePoints);
  return {
    lat: point.lat,
    lng: point.lng,
    source: 'search',
    address: (address ?? '').trim() || base?.address || null,
    distanceKm: centroid ? haversineKm(centroid, point) : null,
    distanceDerivation: centroid ? 'recomputed' : 'unavailable',
    mismatch: null,
    alternate: base
      ? { lat: base.lat, lng: base.lng, source: base.source }
      : null,
  };
}

export function formatMismatchWarning(mismatch: CoordAddressMismatch): string {
  const meters = Math.round(mismatch.distanceMeters);
  return `Declared employer address geocode is ${meters} m from AI coordinates — map uses the declared address; AI may point at headquarters or the wrong street.`;
}
